import type { Rule, RuleContext, RuleResult } from "../types/index.js";
import { logger } from "../utils/logger.js";

/**
 * 组件命名规则
 */
export const componentNamingRule: Rule = {
  name: "component-naming",
  description: "检查组件命名是否符合规范",
  category: "structure",
  severity: "warning",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始组件命名验证`);

    try {
      const node = context.node;
      const issues: string[] = [];
      const suggestions: string[] = [];

      // 检查命名规范
      if (!node.name || node.name.trim().length === 0) {
        issues.push("组件名称不能为空");
      } else {
        // 检查是否包含特殊字符
        if (!/^[a-zA-Z0-9\s\-_/]+$/.test(node.name)) {
          issues.push("组件名称包含不支持的特殊字符");
          suggestions.push("使用字母、数字、空格、连字符或下划线");
        }

        // 检查是否为默认名称
        const defaultNames = ["Frame", "Rectangle", "Ellipse", "Text", "Group"];
        if (defaultNames.some(name => node.name.startsWith(name))) {
          issues.push("使用了默认组件名称");
          suggestions.push("为组件提供有意义的名称");
        }

        // 检查名称长度
        if (node.name.length > 50) {
          issues.push("组件名称过长");
          suggestions.push("保持名称简洁明了");
        }
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 组件命名验证完成: ${valid ? "通过" : "失败"}`);

      return {
        valid,
        message: valid ? "组件命名规范" : `命名问题: ${issues.join(", ")}`,
        severity: "warning",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 组件命名验证失败: ${error}`);
      return {
        valid: false,
        message: `组件命名验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查组件数据格式"]
      };
    }
  }
};

/**
 * 文本内容规则
 */
export const textContentRule: Rule = {
  name: "text-content",
  description: "检查文本内容的质量和可读性",
  category: "structure",
  severity: "info",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始文本内容验证`);

    try {
      const node = context.node;
      const suggestions: string[] = [];

      // 只检查文本节点
      if (node.type !== "TEXT") {
        return {
          valid: true,
          message: "非文本节点，跳过检查",
          severity: "info"
        };
      }

      const text = (node as any).characters || "";
      
      // 检查占位符文本
      const placeholderPatterns = [
        /lorem ipsum/i,
        /placeholder/i,
        /sample text/i,
        /^text$/i,
        /^标题$/i,
        /^内容$/i
      ];

      const hasPlaceholder = placeholderPatterns.some(pattern => pattern.test(text));
      if (hasPlaceholder) {
        suggestions.push("替换占位符文本为实际内容");
      }

      // 检查文本长度
      if (text.length > 200) {
        suggestions.push("考虑分段或缩短文本以提高可读性");
      }

      // 检查空文本
      if (text.trim().length === 0) {
        suggestions.push("文本节点为空，考虑添加内容或删除节点");
      }

      logger.info(`[${traceId}] 文本内容验证完成`);

      return {
        valid: true,
        message: suggestions.length > 0 ? "发现文本优化建议" : "文本内容良好",
        severity: "info",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 文本内容验证失败: ${error}`);
      return {
        valid: false,
        message: `文本内容验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查文本节点数据格式"]
      };
    }
  }
};

/**
 * 层级深度规则
 */
export const layerDepthRule: Rule = {
  name: "layer-depth",
  description: "检查图层嵌套深度是否合理",
  category: "structure",
  severity: "warning",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始层级深度验证`);

    try {
      const depth = calculateDepth(context.node);
      const issues: string[] = [];
      const suggestions: string[] = [];

      if (depth > 10) {
        issues.push(`层级嵌套过深 (${depth} 层)`);
        suggestions.push("考虑重新组织图层结构");
        suggestions.push("使用组件来减少嵌套深度");
      } else if (depth > 6) {
        suggestions.push("层级较深，建议优化结构");
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 层级深度验证完成: 深度=${depth}`);

      return {
        valid,
        message: valid ? `层级深度合理 (${depth} 层)` : `层级深度问题: ${issues.join(", ")}`,
        severity: "warning",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 层级深度验证失败: ${error}`);
      return {
        valid: false,
        message: `层级深度验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查节点结构数据"]
      };
    }
  }
};

/**
 * 元素尺寸规则
 */
export const elementSizeRule: Rule = {
  name: "element-size",
  description: "检查元素尺寸是否合理",
  category: "structure",
  severity: "warning",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始元素尺寸验证`);

    try {
      const node = context.node;
      const bounds = (node as any).absoluteBoundingBox;
      
      if (!bounds) {
        return {
          valid: true,
          message: "无尺寸信息，跳过检查",
          severity: "info"
        };
      }

      const { width, height } = bounds;
      const issues: string[] = [];
      const suggestions: string[] = [];

      // 检查极小尺寸
      if (width < 1 || height < 1) {
        issues.push("元素尺寸过小，可能不可见");
        suggestions.push("调整元素尺寸或删除无用元素");
      }

      // 检查极大尺寸
      if (width > 2000 || height > 2000) {
        issues.push("元素尺寸过大");
        suggestions.push("考虑是否需要如此大的尺寸");
      }

      // 检查极端宽高比
      const aspectRatio = Math.max(width, height) / Math.min(width, height);
      if (aspectRatio > 20) {
        issues.push("元素宽高比极端");
        suggestions.push("检查是否为设计意图");
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 元素尺寸验证完成: ${width}x${height}`);

      return {
        valid,
        message: valid ? `元素尺寸合理 (${width}x${height})` : `尺寸问题: ${issues.join(", ")}`,
        severity: "warning",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 元素尺寸验证失败: ${error}`);
      return {
        valid: false,
        message: `元素尺寸验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查元素边界框数据"]
      };
    }
  }
};

/**
 * 可访问性规则
 */
export const accessibilityRule: Rule = {
  name: "accessibility",
  description: "检查设计的可访问性",
  category: "accessibility",
  severity: "error",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始可访问性验证`);

    try {
      const node = context.node;
      const issues: string[] = [];
      const suggestions: string[] = [];

      // 检查文本对比度（简化检查）
      if (node.type === "TEXT" && (node as any).style) {
        // 这里应该有更复杂的对比度计算
        // 暂时做简单检查
        if ((node as any).style.fontSize && (node as any).style.fontSize < 12) {
          issues.push("文字过小，可能影响可读性");
          suggestions.push("增大字体尺寸至少12px");
        }
      }

      // 检查交互元素尺寸
      if (node.name && (node.name.includes("button") || node.name.includes("按钮"))) {
        const bounds = (node as any).absoluteBoundingBox;
        if (bounds && (bounds.width < 44 || bounds.height < 44)) {
          issues.push("交互元素尺寸过小");
          suggestions.push("交互元素最小尺寸应为44x44px");
        }
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 可访问性验证完成: ${valid ? "通过" : "失败"}`);

      return {
        valid,
        message: valid ? "可访问性检查通过" : `可访问性问题: ${issues.join(", ")}`,
        severity: "error",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 可访问性验证失败: ${error}`);
      return {
        valid: false,
        message: `可访问性验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查节点可访问性属性"]
      };
    }
  }
};

/**
 * 计算节点深度
 */
function calculateDepth(node: any, currentDepth = 0): number {
  if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
    return currentDepth;
  }

  let maxDepth = currentDepth;
  for (const child of node.children) {
    const childDepth = calculateDepth(child, currentDepth + 1);
    maxDepth = Math.max(maxDepth, childDepth);
  }

  return maxDepth;
}